/**
 * Retorna mensagem amigável para exibir ao usuário.
 * O usuário jamais deve ver mensagens técnicas ou stack traces.
 * Use em todos os catch blocks e exibições de erro.
 */

const FRIENDLY_MESSAGES: Record<number, string> = {
  400: 'Os dados enviados estão incorretos. Verifique e tente novamente.',
  401: 'Sessão expirada. Faça login novamente.',
  403: 'Você não tem permissão para esta ação.',
  404: 'Recurso não encontrado.',
  408: 'A requisição demorou muito. Tente novamente.',
  409: 'Conflito com dados existentes. Verifique e tente novamente.',
  422: 'Os dados enviados não puderam ser processados. Verifique e tente novamente.',
  429: 'Muitas tentativas. Aguarde um momento e tente novamente.',
  500: 'Erro no servidor. Tente novamente em instantes ou contate o suporte.',
  502: 'Serviço temporariamente indisponível. Tente novamente em instantes.',
  503: 'Serviço temporariamente indisponível. Tente novamente em instantes.',
};

const NETWORK_ERROR_MESSAGE = 'Falha na conexão. Verifique sua internet e tente novamente.';
const TIMEOUT_ERROR_MESSAGE = 'A requisição demorou muito. Tente novamente.';
const GENERIC_ERROR_MESSAGE = 'Ocorreu um erro inesperado. Tente novamente ou contate o suporte.';

/** Padrões que indicam mensagem técnica do backend — não exibir ao usuário */
const BACKEND_TECHNICAL_PATTERNS: RegExp[] = [
  /^[\w\s]+Error:/i,
  /at\s+\S+\s*\(/,
  /stack\s*trace/i,
  /undefined is not/i,
  /cannot read property/i,
  /token/i,
  /jwt/i,
  /mongoose/i,
  /mongo/i,
  /ECONNREFUSED/i,
  /ETIMEDOUT/i,
  /EAI_AGAIN/i,
  /querySrv/i,
  /ESERVFAIL/i,
  /ENOTFOUND/i,
  /getaddrinfo/i,
  /_tcp\.\S+\.(mongodb|net)/i,
];

/** Mensagens típicas do Axios / runtime — nunca exibir literais */
const NATIVE_TECHNICAL_PATTERNS: RegExp[] = [
  /timeout of \d+ms exceeded/i,
  /^timeout\b/i,
  /Network Error/i,
  /ECONNREFUSED/i,
  /ECONNRESET/i,
  /ECONNABORTED/i,
  /ERR_[A-Z0-9_]+/,
  /^Request failed with status code \d+$/i,
  /^canceled$/i,
  /^aborted$/i,
  /socket hang up/i,
  /Network request failed/i,
];

function isTechnicalBackendMessage(msg: string): boolean {
  const t = msg.trim();
  if (!t || t.length > 200) return true;
  return BACKEND_TECHNICAL_PATTERNS.some((p) => p.test(t));
}

function isTechnicalNativeMessage(msg: string): boolean {
  const t = msg.trim();
  if (!t) return true;
  return NATIVE_TECHNICAL_PATTERNS.some((p) => p.test(t)) || t.length > 200;
}

/** Aceita `message` ou `error` (string) no corpo JSON da API */
function pickBackendUserMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const d = data as { message?: unknown; error?: unknown };
  if (typeof d.message === 'string' && d.message.trim()) return d.message.trim();
  if (typeof d.error === 'string' && d.error.trim()) return d.error.trim();
  return undefined;
}

/**
 * Retorna uma mensagem amigável para exibir ao usuário.
 * Nunca retorna a mensagem técnica bruta do Axios ou stack traces.
 */
export function getFriendlyErrorMessage(error: unknown, fallback?: string): string {
  const defaultMessage = fallback ?? GENERIC_ERROR_MESSAGE;

  if (!error) return defaultMessage;

  const preset = (error as { userMessage?: string }).userMessage;
  if (typeof preset === 'string' && preset.trim()) {
    return preset.trim();
  }

  const err = error as {
    response?: { status?: number; data?: unknown };
    message?: string;
    code?: string;
  };
  const status = err.response?.status;
  const msg = err.message || '';

  const friendlyForStatus = status ? FRIENDLY_MESSAGES[status] : undefined;

  const isNetworkClass =
    err.code === 'ERR_NETWORK' ||
    err.code === 'ERR_SOCKET_NOT_CONNECTED' ||
    err.code === 'ERR_CONNECTION_REFUSED' ||
    err.code === 'ERR_CONNECTION_RESET' ||
    err.code === 'ECONNABORTED' ||
    err.code === 'ERR_SOCKET_TIMEOUT' ||
    err.code === 'ETIMEDOUT' ||
    err.code === 'ERR_EMPTY_RESPONSE' ||
    msg.includes('Network Error') ||
    msg.includes('ECONNREFUSED') ||
    msg.includes('ERR_EMPTY_RESPONSE') ||
    msg.includes('ECONNRESET') ||
    /timeout of \d+ms exceeded/i.test(msg);

  if (isNetworkClass) {
    if (err.code === 'ERR_CANCELED' || /^canceled$/i.test(msg.trim())) {
      return 'Operação cancelada.';
    }
    if (
      /timeout of \d+ms exceeded/i.test(msg) ||
      err.code === 'ECONNABORTED' ||
      err.code === 'ERR_SOCKET_TIMEOUT' ||
      err.code === 'ETIMEDOUT'
    ) {
      return TIMEOUT_ERROR_MESSAGE;
    }
    return NETWORK_ERROR_MESSAGE;
  }

  if (err.code === 'ERR_CANCELED') {
    return 'Operação cancelada.';
  }

  // Resposta HTTP: mensagem do servidor (quando segura) antes do texto genérico por status
  if (err.response) {
    const backendMessage = pickBackendUserMessage(err.response.data);
    if (backendMessage && !isTechnicalBackendMessage(backendMessage)) {
      return backendMessage;
    }
  }

  if (friendlyForStatus) {
    return friendlyForStatus;
  }

  if (typeof msg === 'string' && msg.trim() && !err.response?.status) {
    if (!isTechnicalNativeMessage(msg)) {
      return msg.trim();
    }
  }

  return defaultMessage;
}
